import React, {useEffect} from "react";
import {
    FeatureFlagStrategy,
    FeatureFlagStrategyEnum,
    FeatureFlagStrategySelectorEnum,
} from "../../../types/domain/featureFlagStrategy";
import {
    Box,
    Checkbox,
    Chip,
    ListItemText,
    MenuItem,
    Select,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import {FeatureFlag} from "../../../types/domain/featureFlag";
import {Flag} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {useCompanies} from "../../../hooks/companiesHooks";

interface AddFeatureFlagStrategyProps {
    activeStrategy: FeatureFlagStrategy;
    featureFlag?: FeatureFlag;
    setNewFeatureFlagStrategy: (featureFlagStrategy: FeatureFlagStrategy) => void;
    newFeatureFlagStrategy: FeatureFlagStrategy | null;
}

const AddFeatureFlagStrategy: React.FC<AddFeatureFlagStrategyProps> = ({
    activeStrategy,
    featureFlag,
    setNewFeatureFlagStrategy,
    newFeatureFlagStrategy,
}) => {
    const {t} = useTranslation();
    const companies = useCompanies();

    useEffect(() => {
        if (activeStrategy) {
            setNewFeatureFlagStrategy(JSON.parse(JSON.stringify(activeStrategy)) as FeatureFlagStrategy);
        } else if (featureFlag?.id) {
            setNewFeatureFlagStrategy({
                config: {
                    enabled: false,
                },
                selector: FeatureFlagStrategySelectorEnum.INCLUDE,
                companyIds: [],
                strategy: FeatureFlagStrategyEnum.TOGGLE,
            });
        }
    }, [activeStrategy, featureFlag?.id, setNewFeatureFlagStrategy]);

    return (
        <Stack>
            <Typography
                fontSize={22}
                display={"flex"}
                alignItems="center"
                sx={{textTransform: "uppercase"}}
                color="primary"
            >
                <Flag sx={{marginRight: "12px"}} />

                {featureFlag?.name}
            </Typography>
            <Typography
                fontStyle={"italic"}
                fontSize={14}
                color="text.secondary"
                marginTop={"1rem"}
                marginBottom={"1rem"}
            >
                {featureFlag?.description}
            </Typography>
            <TableContainer sx={{marginTop: "1rem"}}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <Typography fontWeight="bold">{t("feature_flags_page.active")}</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography fontWeight="bold">{t("feature_flags_page.selector")}</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography fontWeight="bold">{t("feature_flags_page.companies")}</Typography>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {newFeatureFlagStrategy ? (
                            <TableRow>
                                <TableCell>
                                    <Switch
                                        checked={newFeatureFlagStrategy.config.enabled}
                                        onChange={(event) => {
                                            setNewFeatureFlagStrategy({
                                                ...newFeatureFlagStrategy,
                                                config: {
                                                    ...newFeatureFlagStrategy.config,
                                                    enabled: event.target.checked,
                                                },
                                            });
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Select
                                        sx={{minWidth: 150}}
                                        value={newFeatureFlagStrategy.selector}
                                        onChange={(e) => {
                                            setNewFeatureFlagStrategy({
                                                ...newFeatureFlagStrategy,
                                                selector: e.target.value as FeatureFlagStrategySelectorEnum,
                                                companyIds: ["ALL", "NONE"].includes(e.target.value)
                                                    ? []
                                                    : newFeatureFlagStrategy.companyIds,
                                            });
                                        }}
                                        displayEmpty
                                    >
                                        {Object.values(FeatureFlagStrategySelectorEnum).map((value) => (
                                            <MenuItem key={value} value={value}>
                                                {t("feature_flags_page." + value)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </TableCell>

                                <TableCell sx={{whiteSpace: "nowrap"}}>
                                    {["INCLUDE", "EXCLUDE"].includes(newFeatureFlagStrategy.selector) ? (
                                        <Select
                                            multiple
                                            value={newFeatureFlagStrategy.companyIds}
                                            onChange={(event) => {
                                                const selectedCompanyIds = event.target.value as string[];
                                                setNewFeatureFlagStrategy({
                                                    ...newFeatureFlagStrategy,
                                                    companyIds: selectedCompanyIds,
                                                });
                                            }}
                                            renderValue={(selected) => selected?.length}
                                            sx={{minWidth: 200}}
                                        >
                                            {companies.isSuccess
                                                ? companies.data.map((company) => (
                                                      <MenuItem key={company.id} value={company.id}>
                                                          <Checkbox
                                                              checked={newFeatureFlagStrategy.companyIds.includes(
                                                                  company.id,
                                                              )}
                                                          />
                                                          <ListItemText primary={company?.properties?.name} />
                                                      </MenuItem>
                                                  ))
                                                : ""}
                                        </Select>
                                    ) : (
                                        <Box sx={{width: "200px"}}>
                                            {t("feature_flags_page." + newFeatureFlagStrategy.selector)}
                                        </Box>
                                    )}
                                </TableCell>
                            </TableRow>
                        ) : (
                            ""
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {companies.isSuccess ? (
                <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    marginTop={"1rem"}
                    sx={{
                        overflowY: "auto",
                        maxHeight: "200px",
                    }}
                >
                    {newFeatureFlagStrategy
                        ? newFeatureFlagStrategy.companyIds.map((companyId) => {
                              const company = companies?.data?.find((c) => c.id === companyId);
                              return company ? (
                                  <Chip
                                      key={companyId}
                                      label={company?.properties?.name}
                                      sx={{
                                          gap: "4px",
                                          marginTop: "8px !important",
                                      }}
                                  />
                              ) : null;
                          })
                        : ""}
                </Stack>
            ) : (
                ""
            )}
        </Stack>
    );
};

export default AddFeatureFlagStrategy;
